import * as THREE from 'three';
import type {
  ActiveSelectionTool,
  SelectionDragSession,
  SelectionToolContext,
} from './activeSelectionTool';
import { computeApproximateLocalScaleFactors } from '@/utils/mathUtils';

type Axis = 'x' | 'y' | 'z';
type Corner = 'pos' | 'neg';

type ResizeDragState = {
  mode: 'axis';
  axisName: Axis;
  axis: THREE.Vector3;
  plane: THREE.Plane;
  startPoint: THREE.Vector3;
  origin: THREE.Vector3;
  baseExtent: number;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
    scale: THREE.Vector3;
    quaternion: THREE.Quaternion;
    offsetFromOrigin: THREE.Vector3;
  }>;
};

type CornerResizeDragState = {
  mode: 'corner';
  plane: THREE.Plane;
  origin: THREE.Vector3;
  baseDeltaX: number;
  baseDeltaZ: number;
  initialStates: Array<{
    object: SelectionToolContext['selectedObjects'][number];
    position: THREE.Vector3;
    scale: THREE.Vector3;
    quaternion: THREE.Quaternion;
  }>;
};

type ResizeDragSessionState = ResizeDragState | CornerResizeDragState;

const AXIS_VECTORS: Record<Axis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

const MIN_SCALE_COMPONENT = 0.05;
const SCALE_SNAP_STEP = 0.1;

export class ResizeSelectionTool implements ActiveSelectionTool {
  readonly id = 'resize' as const;

  private readonly center = new THREE.Vector3();
  private readonly size = new THREE.Vector3();
  private readonly gizmoRoot = new THREE.Group();
  private readonly pickTargets: THREE.Object3D[] = [];
  private readonly cornerHandles: Array<{
    corner: Corner;
    visual: THREE.Mesh;
    pick: THREE.Mesh;
  }> = [];
  private visible = false;

  constructor() {
    this.gizmoRoot.visible = false;
    this.createResizeGizmo();
  }

  render(parent: THREE.Object3D): void {
    parent.add(this.gizmoRoot);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.gizmoRoot.visible = visible;
  }

  getPickTargets(): THREE.Object3D[] {
    if (!this.visible) {
      return [];
    }

    return this.pickTargets;
  }

  updateTransform(center: THREE.Vector3, size: THREE.Vector3): void {
    this.center.copy(center);
    this.size.copy(size);

    const radius = Math.max(size.length() * 0.32, 1.1);
    this.gizmoRoot.position.copy(this.center);
    this.gizmoRoot.scale.setScalar(radius);
    this.updateCornerHandlePositions(radius);
  }

  startDrag(context: SelectionToolContext): SelectionDragSession | null {
    const corner = context.intersection.object.userData.corner as
      | Corner
      | undefined;
    if (corner) {
      return this.startCornerDrag(context, corner);
    }

    const axisName = context.intersection.object.userData.axis as
      | Axis
      | undefined;
    if (!axisName) {
      return null;
    }

    const axis = AXIS_VECTORS[axisName].clone();
    const cameraDirection = new THREE.Vector3();
    context.camera.getWorldDirection(cameraDirection);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      cameraDirection.normalize(),
      this.center,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const baseExtent = Math.max(this.size[axisName], 0.5);
    const origin = this.center.clone();
    if (axisName === 'y') {
      origin.y = this.center.y - this.size.y * 0.5;
    }

    const dragState: ResizeDragState = {
      mode: 'axis',
      axisName,
      axis,
      plane,
      startPoint: startPoint.clone(),
      origin,
      baseExtent,
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
        scale: object.scale.clone(),
        quaternion: object.quaternion.clone(),
        offsetFromOrigin: object.position.clone().sub(origin),
      })),
    };

    return this.createDragSession(dragState);
  }

  dispose(): void {
    for (const child of this.gizmoRoot.children) {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
  }

  private createDragSession(
    dragState: ResizeDragSessionState,
  ): SelectionDragSession {
    return {
      updateDrag: (ray, snapToHalfStep) => {
        if (dragState.mode === 'corner') {
          const dragPoint = ray.intersectPlane(
            dragState.plane,
            new THREE.Vector3(),
          );
          if (!dragPoint) {
            return;
          }

          let targetCornerX = dragPoint.x;
          let targetCornerZ = dragPoint.z;

          if (snapToHalfStep) {
            targetCornerX = Math.round(targetCornerX / 0.5) * 0.5;
            targetCornerZ = Math.round(targetCornerZ / 0.5) * 0.5;
          }

          let factorX =
            (targetCornerX - dragState.origin.x) / dragState.baseDeltaX;
          let factorZ =
            (targetCornerZ - dragState.origin.z) / dragState.baseDeltaZ;

          factorX = Math.max(MIN_SCALE_COMPONENT, factorX);
          factorZ = Math.max(MIN_SCALE_COMPONENT, factorZ);

          for (const state of dragState.initialStates) {
            const newX =
              dragState.origin.x +
              (state.position.x - dragState.origin.x) * factorX;
            const newZ =
              dragState.origin.z +
              (state.position.z - dragState.origin.z) * factorZ;

            const localScaleFactors = computeApproximateLocalScaleFactors(
              state.scale,
              state.quaternion,
              [
                {
                  axis: AXIS_VECTORS.x,
                  factor: factorX,
                },
                {
                  axis: AXIS_VECTORS.z,
                  factor: factorZ,
                },
              ],
            );

            state.object.setPosition(newX, state.position.y, newZ);
            state.object.setScale(
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.x * localScaleFactors.x,
              ),
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.y * localScaleFactors.y,
              ),
              Math.max(
                MIN_SCALE_COMPONENT,
                state.scale.z * localScaleFactors.z,
              ),
            );
          }

          return;
        }

        const dragPoint = ray.intersectPlane(
          dragState.plane,
          new THREE.Vector3(),
        );
        if (!dragPoint) {
          return;
        }

        const delta = dragState.axis.dot(
          dragPoint.clone().sub(dragState.startPoint),
        );

        let factor = 1 + delta / dragState.baseExtent;
        factor = Math.max(MIN_SCALE_COMPONENT, factor);

        if (snapToHalfStep) {
          factor = Math.round(factor / SCALE_SNAP_STEP) * SCALE_SNAP_STEP;
          factor = Math.max(MIN_SCALE_COMPONENT, factor);
        }

        for (const state of dragState.initialStates) {
          const parallelDistance = state.offsetFromOrigin.dot(dragState.axis);
          const parallel = dragState.axis
            .clone()
            .multiplyScalar(parallelDistance);
          const perpendicular = state.offsetFromOrigin.clone().sub(parallel);
          const scaledParallel = parallel.multiplyScalar(factor);
          const newOffset = perpendicular.add(scaledParallel);
          const newPosition = dragState.origin.clone().add(newOffset);

          const localScaleFactors = computeApproximateLocalScaleFactors(
            state.scale,
            state.quaternion,
            [
              {
                axis: dragState.axis,
                factor,
              },
            ],
          );

          const nextScale = new THREE.Vector3(
            Math.max(MIN_SCALE_COMPONENT, state.scale.x * localScaleFactors.x),
            Math.max(MIN_SCALE_COMPONENT, state.scale.y * localScaleFactors.y),
            Math.max(MIN_SCALE_COMPONENT, state.scale.z * localScaleFactors.z),
          );

          state.object.setPosition(newPosition.x, newPosition.y, newPosition.z);
          state.object.setScale(nextScale.x, nextScale.y, nextScale.z);
        }
      },
    };
  }

  private startCornerDrag(
    context: SelectionToolContext,
    corner: Corner,
  ): SelectionDragSession | null {
    const halfSize = this.size.clone().multiplyScalar(0.5);
    const minCorner = this.center.clone().sub(halfSize);
    const maxCorner = this.center.clone().add(halfSize);

    const draggedCorner =
      corner === 'pos'
        ? new THREE.Vector3(maxCorner.x, minCorner.y, maxCorner.z)
        : new THREE.Vector3(minCorner.x, minCorner.y, minCorner.z);
    const origin =
      corner === 'pos'
        ? new THREE.Vector3(minCorner.x, minCorner.y, minCorner.z)
        : new THREE.Vector3(maxCorner.x, minCorner.y, maxCorner.z);

    const baseDeltaX = draggedCorner.x - origin.x;
    const baseDeltaZ = draggedCorner.z - origin.z;
    if (Math.abs(baseDeltaX) < 1e-4 || Math.abs(baseDeltaZ) < 1e-4) {
      return null;
    }

    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(0, 1, 0),
      draggedCorner,
    );

    const startPoint = context.ray.intersectPlane(plane, new THREE.Vector3());
    if (!startPoint) {
      return null;
    }

    const dragState: CornerResizeDragState = {
      mode: 'corner',
      plane,
      origin,
      baseDeltaX,
      baseDeltaZ,
      initialStates: context.selectedObjects.map(object => ({
        object,
        position: object.position.clone(),
        scale: object.scale.clone(),
        quaternion: object.quaternion.clone(),
      })),
    };

    return this.createDragSession(dragState);
  }

  private createResizeGizmo() {
    const axisConfigs: Array<{
      axis: Axis;
      color: number;
      rotation: THREE.Euler;
    }> = [
      {
        axis: 'x',
        color: 0xff9266,
        rotation: new THREE.Euler(0, 0, -Math.PI / 2),
      },
      {
        axis: 'y',
        color: 0x7bec86,
        rotation: new THREE.Euler(0, 0, 0),
      },
      {
        axis: 'z',
        color: 0x7daeff,
        rotation: new THREE.Euler(Math.PI / 2, 0, 0),
      },
    ];

    for (const config of axisConfigs) {
      const group = new THREE.Group();
      group.rotation.copy(config.rotation);

      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, 1.2, 8),
        new THREE.MeshBasicMaterial({
          color: config.color,
          depthTest: false,
          transparent: true,
          opacity: 0.88,
        }),
      );
      stem.position.y = 0.55;
      stem.renderOrder = 1000;

      const knob = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.18, 0.18),
        new THREE.MeshBasicMaterial({
          color: config.color,
          depthTest: false,
        }),
      );
      knob.position.y = 1.18;
      knob.renderOrder = 1001;

      const pickHandle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.14, 0.14, 1.6, 8),
        new THREE.MeshBasicMaterial({
          visible: false,
          depthTest: false,
        }),
      );
      pickHandle.position.y = 0.8;
      pickHandle.userData.axis = config.axis;
      pickHandle.userData.dragPriority = 1;

      group.add(stem);
      group.add(knob);
      group.add(pickHandle);
      this.gizmoRoot.add(group);
      this.pickTargets.push(pickHandle);
    }

    const cornerConfigs: Array<{
      corner: Corner;
    }> = [
      {
        corner: 'pos',
      },
      {
        corner: 'neg',
      },
    ];

    for (const config of cornerConfigs) {
      const cornerVisual = new THREE.Mesh(
        new THREE.BoxGeometry(0.16, 0.16, 0.16),
        new THREE.MeshBasicMaterial({
          color: 0xf4d35e,
          depthTest: false,
          transparent: true,
          opacity: 0.95,
        }),
      );
      cornerVisual.renderOrder = 1002;

      const cornerPick = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 10, 10),
        new THREE.MeshBasicMaterial({
          visible: false,
          depthTest: false,
        }),
      );
      cornerPick.userData.corner = config.corner;
      cornerPick.userData.dragPriority = 2;

      this.gizmoRoot.add(cornerVisual);
      this.gizmoRoot.add(cornerPick);
      this.pickTargets.push(cornerPick);
      this.cornerHandles.push({
        corner: config.corner,
        visual: cornerVisual,
        pick: cornerPick,
      });
    }
  }

  private updateCornerHandlePositions(radius: number) {
    const halfSize = this.size.clone().multiplyScalar(0.5);

    for (const handle of this.cornerHandles) {
      const cornerOffset =
        handle.corner === 'pos'
          ? new THREE.Vector3(halfSize.x, -halfSize.y, halfSize.z)
          : new THREE.Vector3(-halfSize.x, -halfSize.y, -halfSize.z);

      const localCornerPosition = cornerOffset.divideScalar(radius);
      handle.visual.position.copy(localCornerPosition);
      handle.pick.position.copy(localCornerPosition);
    }
  }
}
